import { trackProductInfoProductDescriptionAccordionExpandDetailsSectionClick } from "@faire/web-api/events/productInfo/click/productDescriptionAccordionExpandDetailsSection";
import { IProductOption } from "@faire/web-api/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import React, { useMemo } from "react";

import { useSettingEnablePdpAttributesEnrichment } from "@faire/retailer/settings/useSettingEnablePdpAttributesEnrichment";

import {
  getProductDetailsEntriesAttributes,
  getProductDetailsEntriesTexts,
} from "./__internal__/utils";
import { AccordionContainer } from "./AccordionContainer";
import { ListAccordionContainer } from "./ListAccordionContainer";

type Props = ProductDetailsAccordion.IProps;

const INITIAL_NUMBER_OF_LINES_SHOWN = 5;
const MAX_NUMBER_OF_LINES_SHOWN_PER_DETAIL = 2;

export const ProductDetailsAccordion: React.FC<Props> = ({
  productOption,
  detailsInformationSection,
  maxPreviewLines,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const pdpAttributesEnrichmentEnabled =
    useSettingEnablePdpAttributesEnrichment();

  const title = strictLocalize({
    defaultMessage: "Product details",
    description: {
      text: "section title. Text of button to expand the details of the product, like dimensions, made in",
    },
  });

  const productOptionLevelEntries = useMemo(() => {
    const productOptionToken = productOption?.token;
    if (!productOptionToken) {
      return [];
    }
    return detailsInformationSection?.option_level_entries_by_option_token?.[
      productOptionToken
    ]?.entries;
  }, [
    detailsInformationSection?.option_level_entries_by_option_token,
    productOption?.token,
  ]);

  const contents = useMemo(() => {
    if (!detailsInformationSection) {
      return [];
    }

    return [
      ...getProductDetailsEntriesTexts(detailsInformationSection.entries),
      // We display option level entries at the end
      ...getProductDetailsEntriesTexts(productOptionLevelEntries),
    ];
  }, [detailsInformationSection, productOptionLevelEntries]);

  const productDetailsList = useMemo(() => {
    if (!detailsInformationSection) {
      return [];
    }

    return [
      ...getProductDetailsEntriesAttributes(detailsInformationSection.entries),
      ...getProductDetailsEntriesAttributes(productOptionLevelEntries),
    ];
  }, [detailsInformationSection, productOptionLevelEntries]);

  if (pdpAttributesEnrichmentEnabled) {
    if (productDetailsList.length === 0) {
      return null;
    }
    return (
      <ListAccordionContainer
        title={title}
        productDetails={productDetailsList}
        maxNumberOfDetailsShownWhenCollapsed={
          maxPreviewLines ?? INITIAL_NUMBER_OF_LINES_SHOWN
        }
        maxNumberOfLinesShownPerDetail={MAX_NUMBER_OF_LINES_SHOWN_PER_DETAIL}
        onExpandAccordionItem={
          trackProductInfoProductDescriptionAccordionExpandDetailsSectionClick
        }
      />
    );
  } else {
    if (contents.length === 0) {
      return null;
    }
    return (
      <AccordionContainer
        title={title}
        text={contents.join("\n")}
        isListOfItems
        maxNumberOfLinesShownWhenCollapsed={
          maxPreviewLines ?? INITIAL_NUMBER_OF_LINES_SHOWN
        }
        onExpandAccordionItem={
          trackProductInfoProductDescriptionAccordionExpandDetailsSectionClick
        }
      />
    );
  }
};

export namespace ProductDetailsAccordion {
  export interface IProps {
    className?: string;
    productOption: IProductOption | undefined;
    detailsInformationSection:
      | IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection
      | undefined;
    maxPreviewLines?: number;
  }
}
